import React, { useState, useContext, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Text, Progress, Box, VStack, Select,
  Checkbox,
  Grid,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { FaFileUpload } from 'react-icons/fa';
import { FiInfo, FiChevronDown, FiChevronRight, FiPaperclip, FiUploadCloud } from 'react-icons/fi';
import { processSheedData } from './process_sheet_data';
import { AuditSheetContext } from '../../../../providers/AuditSheetProvider';
import WorkerBuilder from './workBulider';
import AttachWorkerBuilder from './attachWorkBuilder';
import { UPLOAD_AUDITED_CHART } from '../../../../constants/Endpoints';
import { ACCESS_TOKEN } from '../../../../constants/PreferenceKeys';

function ExcelReader({ updateAuditSheet, providerOptions, currentUpload }) {
  const auditSheetContext = useContext(AuditSheetContext);
  const { industryCodes, iCDCodes, industryCodesLoaded, icdCodesLoaded, responseAuditIssues } = auditSheetContext;
  const codeListsLoaded = industryCodesLoaded && icdCodesLoaded;

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImported, setIsImported] = useState(false);
  const [isAttached, setIsAttached] = useState(false);
  const [error, setError] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState('');
  const [worker, setWorker] = useState(null);
  const [attachWorker, setAttachWorker] = useState(null);
  const [jsonHeaders, setJsonHeaders] = useState([]);
  const [json, setJSON] = useState([]);
  const [headerMapping, setHeaderMapping] = useState({});
  const [showMappingUI, setShowMappingUI] = useState(false);
  const [isImportFile, setIsImportFile] = useState(true);
  const [isAttachFile, setIsAttachFile] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [checkboxState, setCheckboxState] = useState({
    CPTCommaSeparated: true,
    cptModifierAttached: true,
    ICDCommaSeparated: true,
  });
  const [isAtaching, setIsAttaching] = useState(false);
  const [atachingMsg, setAttachingMsg] = useState(null);
  const [attachingProgress, setAttachingProgress] = useState(0);
  const [additionalIcdMappings, setAdditionalIcdMappings] = useState([]);
  const [additionalCptMappings, setAdditionalCptMappings] = useState([]);
  const [additionalModifierMappings, setAdditionalModifierMappings] = useState([]);

  // ── Audited section ───────────────────────────────────────────────────────
  const [showAuditedSection, setShowAuditedSection] = useState(false);
  // '' | 'importColumns' | 'agreeColumn' | 'agreeAll'
  const [icdAuditedMode, setIcdAuditedMode] = useState('');
  const [cptAuditedMode, setCptAuditedMode] = useState('');
  const [auditedIcdCommaSeparated, setAuditedIcdCommaSeparated] = useState(true);
  const [additionalAuditedIcdMappings, setAdditionalAuditedIcdMappings] = useState([]);
  const [auditedCptCommaSeparated, setAuditedCptCommaSeparated] = useState(true);
  const [auditedCptModifierAttached, setAuditedCptModifierAttached] = useState(true);
  const [additionalAuditedCptMappings, setAdditionalAuditedCptMappings] = useState([]);
  const [additionalAuditedModifierMappings, setAdditionalAuditedModifierMappings] = useState([]);
  const [showRvuSection, setShowRvuSection] = useState(false);

  const encounterHeaders = ['Encounter #', 'Rendering', 'Enc Dt'];
  const billedCodeHeaders = ['Billed ICD', 'Billed CPT'];
  const cardTitles = {
    'Billed CPT': 'Billed CPT & Modifier',
  };
  const additionalHeaders = [
    'Response', 'Notes', 'Provider Education',
    'Number & Complexity', 'Amount & Complexity', 'Mortality of Patient Mgmt',
  ];

  const headerTooltips = {
    'Billed ICD': (
      <Box fontSize="xs" p={1} maxW="260px">
        <Text fontWeight="semibold" mb={0.5}>Comma Separated</Text>
        <Text mb={2} color="gray.200">Use when multiple ICD codes share one cell, e.g. "Z87.39, I10, E11.9".</Text>
        <Text fontWeight="semibold" mb={0.5}>+ Add ICD Column</Text>
        <Text color="gray.200">Maps an additional Excel column as a separate ICD slot when codes are split across columns.</Text>
      </Box>
    ),
    'Billed CPT': (
      <Box fontSize="xs" p={1} maxW="260px">
        <Text fontWeight="semibold" mb={0.5}>Modifier attached to CPT</Text>
        <Text mb={2} color="gray.200">Checked: modifiers travel with the CPT itself, e.g. "99213-25". Unchecked: modifiers come from a separate column, matched by position to each CPT.</Text>
        <Text fontWeight="semibold" mb={0.5}>Comma Separated</Text>
        <Text mb={2} color="gray.200">Use when multiple CPT codes share one cell, e.g. "99213, 99214". If modifiers aren't attached, map a Modifier column that's also comma-separated in the same order — leave a slot blank for a CPT with no modifier, e.g. "25, , 59".</Text>
        <Text fontWeight="semibold" mb={0.5}>Units</Text>
        <Text mb={2} color="gray.200">Embed units in the CPT value using code*units format, e.g. 99213*2 means 2 units of 99213 — works regardless of where the modifier sits.</Text>
        <Text fontWeight="semibold" mb={0.5}>+ Add CPT Column</Text>
        <Text color="gray.200">Maps an additional Excel column as a separate CPT slot when codes are split across columns. When modifiers aren't attached, each added CPT column gets its own paired Modifier column.</Text>
      </Box>
    ),
    'Audited ICD': (
      <Box fontSize="xs" p={1} maxW="260px">
        <Text fontWeight="semibold" mb={0.5}>Comma Separated</Text>
        <Text mb={2} color="gray.200">Use when multiple audited ICD codes share one cell, e.g. "Z87.39, I10, E11.9".</Text>
        <Text fontWeight="semibold" mb={0.5}>+ Add Audited ICD Column</Text>
        <Text color="gray.200">Maps an additional Excel column as a separate audited ICD slot when codes are split across columns.</Text>
      </Box>
    ),
    'Audited CPT & Modifier': (
      <Box fontSize="xs" p={1} maxW="260px">
        <Text fontWeight="semibold" mb={0.5}>Modifier attached to CPT</Text>
        <Text mb={2} color="gray.200">Checked: modifiers travel with the CPT itself, e.g. "99213-25". Unchecked: modifiers come from a separate column, matched by position to each CPT.</Text>
        <Text fontWeight="semibold" mb={0.5}>Comma Separated</Text>
        <Text mb={2} color="gray.200">Use when multiple audited CPT codes share one cell, e.g. "99213, 99214". If modifiers aren't attached, map an Audited Modifier column that's also comma-separated in the same order — leave a slot blank for a CPT with no modifier, e.g. "25, , 59".</Text>
        <Text fontWeight="semibold" mb={0.5}>Units</Text>
        <Text mb={2} color="gray.200">Embed units in the CPT value using code*units format, e.g. 99213*2 means 2 units of 99213 — works regardless of where the modifier sits.</Text>
        <Text fontWeight="semibold" mb={0.5}>+ Add Audited CPT & Modifier Column</Text>
        <Text color="gray.200">Maps an additional Excel column as a separate audited CPT slot when codes are split across columns. When modifiers aren't attached, each added CPT column gets its own paired Modifier column.</Text>
      </Box>
    ),
    'Response': (
      <Box fontSize="xs" p={1} maxW="260px">
        <Text fontWeight="semibold" mb={0.5}>Issue / Response Column</Text>
        <Text mb={2} color="gray.200">Maps your Excel column containing issue names to the Response field. Each cell can hold multiple issues separated by commas, matched by name against that row's available response options — including dynamic ones like "Incorrect Billed CPT: 99213", generated from that row's own codes.</Text>
        <Text fontWeight="semibold" mb={0.5}>Agree Audits</Text>
        <Text mb={2} color="gray.200">Type "Agree Audits" alone in the cell to mark every audit agreed for that row, same as the grid's own checkbox.</Text>
        <Text color="gray.200">Issues are taken in the order written, up to a 10-point weight cap. An entry that would push the total over 10 is skipped — later entries are still checked and taken if they fit within what's left of the cap. Unmatched or skipped entries are silent — no warning shown, so large imports aren't interrupted.</Text>
      </Box>
    ),
    'Billed RVU': (
      <Box fontSize="xs" p={1} maxW="260px">
        <Text fontWeight="semibold" mb={0.5}>Optional — Auto-Calculated</Text>
        <Text mb={2} color="gray.200">Billed RVU is automatically calculated from your CPT codes using the CMS fee schedule. You do not need to map this column for normal imports.</Text>
        <Text color="gray.200">Only map this if you want to override the calculated value with a specific figure from your Excel file.</Text>
      </Box>
    ),
    'Billed $ Value': (
      <Box fontSize="xs" p={1} maxW="260px">
        <Text fontWeight="semibold" mb={0.5}>Optional — Auto-Calculated</Text>
        <Text mb={2} color="gray.200">Billed dollar value is automatically calculated from your CPT codes using the CMS physician fee schedule (par amount). You do not need to map this column for normal imports.</Text>
        <Text color="gray.200">Accepts plain numbers or dollar-formatted values such as $1,234.56 — the $ sign and commas are handled automatically.</Text>
      </Box>
    ),
  };

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setIsOpen(false);
    resetState();
  };

  const resetState = () => {
    setIsLoading(false);
    setIsImported(false);
    setIsAttached(false);
    setError(null);
    setProgressMessage('');
    setProgress(0);
    setFileName('');
    setJsonHeaders([]);
    setJSON([]);
    setHeaderMapping({});
    setShowMappingUI(false);
    setIsImportFile(true);
    setIsAttachFile(false);
    setIsDragActive(false);
    setCheckboxState({
      CPTCommaSeparated: true,
      cptModifierAttached: true,
      ICDCommaSeparated: true,
    });
    setAttachingMsg('');
    setIsAttaching(false);
    setAttachingProgress(0);
    setAdditionalIcdMappings([]);
    setAdditionalCptMappings([]);
    setAdditionalModifierMappings([]);
    setShowAuditedSection(false);
    setShowRvuSection(false);
    setIcdAuditedMode('');
    setCptAuditedMode('');
    setAuditedIcdCommaSeparated(true);
    setAdditionalAuditedIcdMappings([]);
    setAuditedCptCommaSeparated(true);
    setAuditedCptModifierAttached(true);
    setAdditionalAuditedCptMappings([]);
    setAdditionalAuditedModifierMappings([]);
  };

  useEffect(() => {
    if (attachWorker) {
      attachWorker.onmessage = async (e) => {
        const { success, data, error } = e.data;
        if (!success) {
          setIsAttaching(false);
          setError(error || 'Error while attaching the file.');
          setAttachingProgress(0);
          attachWorker.terminate();
          setAttachWorker(null);
          return;
        }
        setAttachingMsg("File Attached Successfully");
        setAttachingProgress(100);
        setIsAttaching(false);
        setIsAttached(true);
      };

      attachWorker.onerror = (e) => {
        setIsAttaching(false);
        setError('Error in processing worker.');
        setAttachingProgress(0);
        attachWorker.terminate();
        setAttachWorker(null);
      };
    } else {
      const newAttachWorker = new AttachWorkerBuilder();
      setAttachWorker(newAttachWorker);
    }
  }, [attachWorker]);

  useEffect(() => {
    if (worker) {
      worker.onmessage = async (e) => {
        const { success, json, error } = e.data;

        if (!success) {
          setError(error || 'Error while processing the file.');
          setProgress(0);
          setIsLoading(false);
          worker.terminate();
          setWorker(null);
          return;
        }
        const filteredJson = json.map(row => {
          return Object.entries(row)
            .filter(([key, _]) => !key.startsWith("__EMPTY"))
            .reduce((acc, [key, value]) => {
              acc[key] = value === "" ? null : value;
              return acc;
            }, {});
        });
        if (!filteredJson || filteredJson.length === 0) {
          setError('No valid data found in the file. Please check the file and try again.');
          setProgress(0);
          setIsLoading(false);
          worker.terminate();
          setWorker(null);
          return;
        }
        setJSON(filteredJson);
        setJsonHeaders(Object.keys(filteredJson[0]));
        setShowMappingUI(true);
        setIsLoading(false);
      };

      worker.onerror = (e) => {
        setError('Error in processing worker.');
        setProgress(0);
        setIsLoading(false);
        worker.terminate();
        setWorker(null);
      };
    } else {
      const newWorker = new WorkerBuilder();
      setWorker(newWorker);
    }
  }, [worker]);

  const addAdditionalIcdColumn = () => setAdditionalIcdMappings(prev => [...prev, '']);
  const removeAdditionalIcdColumn = (idx) => setAdditionalIcdMappings(prev => prev.filter((_, i) => i !== idx));
  const handleAdditionalIcdChange = (idx, value) => setAdditionalIcdMappings(prev => prev.map((v, i) => i === idx ? value : v));

  const handleAdditionalCptChange = (idx, value) => setAdditionalCptMappings(prev => prev.map((v, i) => i === idx ? value : v));
  const handleAdditionalModifierChange = (idx, value) => setAdditionalModifierMappings(prev => prev.map((v, i) => i === idx ? value : v));

  // CPT and Modifier columns are added/removed together, at the same index — this keeps
  // the two lists structurally 1:1, matching how map-columns mode is parsed.
  const addAdditionalCptModifierColumn = () => {
    setAdditionalCptMappings(prev => [...prev, '']);
    setAdditionalModifierMappings(prev => [...prev, '']);
  };
  const removeAdditionalCptModifierColumn = (idx) => {
    setAdditionalCptMappings(prev => prev.filter((_, i) => i !== idx));
    setAdditionalModifierMappings(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Audited mode change handlers (clears stale selections on mode switch) ──
  const handleIcdAuditedModeChange = (newMode) => {
    setIcdAuditedMode(newMode);
    setAdditionalAuditedIcdMappings([]);
    setAuditedIcdCommaSeparated(true);
    setHeaderMapping(prev => { const n = { ...prev }; delete n['Audited ICD']; delete n['ICD Agree']; return n; });
  };
  const handleCptAuditedModeChange = (newMode) => {
    setCptAuditedMode(newMode);
    setAdditionalAuditedCptMappings([]);
    setAdditionalAuditedModifierMappings([]);
    setAuditedCptCommaSeparated(true);
    setAuditedCptModifierAttached(true);
    setHeaderMapping(prev => { const n = { ...prev }; delete n['Audited CPT']; delete n['Audited Modifier']; delete n['CPT Agree']; return n; });
  };

  // ── Audited additional-column handlers ────────────────────────────────────
  const addAdditionalAuditedIcdColumn = () => setAdditionalAuditedIcdMappings(prev => [...prev, '']);
  const removeAdditionalAuditedIcdColumn = (idx) => setAdditionalAuditedIcdMappings(prev => prev.filter((_, i) => i !== idx));
  const handleAdditionalAuditedIcdChange = (idx, value) => setAdditionalAuditedIcdMappings(prev => prev.map((v, i) => i === idx ? value : v));

  const handleAdditionalAuditedCptChange = (idx, value) => setAdditionalAuditedCptMappings(prev => prev.map((v, i) => i === idx ? value : v));
  const handleAdditionalAuditedModifierChange = (idx, value) => setAdditionalAuditedModifierMappings(prev => prev.map((v, i) => i === idx ? value : v));

  const addAdditionalAuditedCptModifierColumn = () => {
    setAdditionalAuditedCptMappings(prev => [...prev, '']);
    setAdditionalAuditedModifierMappings(prev => [...prev, '']);
  };
  const removeAdditionalAuditedCptModifierColumn = (idx) => {
    setAdditionalAuditedCptMappings(prev => prev.filter((_, i) => i !== idx));
    setAdditionalAuditedModifierMappings(prev => prev.filter((_, i) => i !== idx));
  };

  const processSelectedFile = (file) => {
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setError('Unsupported file type. Please choose a .xlsx or .xls file.');
      return;
    }

    if (isImportFile) {
      setFileName(file.name);
      setIsLoading(true);
      setProgressMessage('Extracting headers from the file...');
      setProgress(10);
      worker.postMessage({ file });
    }
    if (isAttachFile) {
      if (file.size === 0) {
        setError('The selected file is empty. Please choose a valid Excel file.');
        return;
      }
      setAttachingMsg("Attaching File Please Wait...");
      setFileName(file.name);
      setIsAttaching(true);
      setAttachingProgress(10);
      let token = localStorage.getItem(ACCESS_TOKEN);
      const formDataObject = {
        upload_audited_chart: file,
        chart_id: currentUpload.id,
      };
      attachWorker.postMessage({ UPLOAD_AUDITED_CHART, token, formDataObject });
      setAttachingProgress(50);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    processSelectedFile(file);
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    if (!isImportFile && !isAttachFile) return;
    processSelectedFile(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!isImportFile && !isAttachFile) return;
    setIsDragActive(true);
  };

  const handleDragLeave = () => setIsDragActive(false);

  const handleHeaderMappingChange = (originalHeader, mappedHeader) => {
    setHeaderMapping((prev) => ({
      ...prev,
      [originalHeader]: mappedHeader,
    }));
  };

  const getMappedPrefixes = () => {
    const prefixes = new Set(['chart_id', 'file_name']);

    if (headerMapping['Encounter #']) prefixes.add('encounter_no');
    if (headerMapping['Rendering'])   prefixes.add('rendering');
    if (headerMapping['Enc Dt'])      prefixes.add('enc_dt');
    if (headerMapping['Billed RVU'])              prefixes.add('provider_rvu');
    if (headerMapping['Billed $ Value'])          prefixes.add('provider_dollar_value');
    if (headerMapping['Audited RVU'])             prefixes.add('audited_rvu');
    if (headerMapping['Audited $ Value'])         prefixes.add('audited_dollar_value');
    if (headerMapping['Response'])                prefixes.add('response');
    if (headerMapping['Notes'])                   prefixes.add('notes');
    if (headerMapping['Provider Education'])      prefixes.add('provider_education');
    if (headerMapping['Number & Complexity'])      prefixes.add('number_complexity');
    if (headerMapping['Amount & Complexity'])      prefixes.add('amount_complexity');
    if (headerMapping['Mortality of Patient Mgmt']) prefixes.add('risk_complications');

    if (headerMapping['Billed ICD']) {
      prefixes.add('billed_icd');
    }

    if (headerMapping['Billed CPT']) {
      prefixes.add('srvcs_no');
      // Modifier may come from an attached hyphen or a separate column — either way,
      // once CPT is mapped, the import owns this field.
      prefixes.add('billed_modifier');
      prefixes.add('provider_rvu');
      prefixes.add('provider_dollar_value');
    }

    // Audited side — only one mode active per category
    if (icdAuditedMode === 'importColumns' && headerMapping['Audited ICD']) {
      prefixes.add('audited_icd');
      prefixes.add('icd_agree');
      prefixes.add('icd_disagree');
    } else if (icdAuditedMode === 'agreeColumn' && headerMapping['ICD Agree']) {
      prefixes.add('icd_agree');
      prefixes.add('icd_disagree');
      prefixes.add('audited_icd');
    } else if (icdAuditedMode === 'agreeAll') {
      prefixes.add('icd_agree');
      prefixes.add('icd_disagree');
      prefixes.add('audited_icd');
    }

    if (cptAuditedMode === 'importColumns' && headerMapping['Audited CPT']) {
      prefixes.add('audited_cpt');
      prefixes.add('audited_modifier');
      prefixes.add('agree');
      prefixes.add('disagree');
    } else if (cptAuditedMode === 'agreeColumn' && headerMapping['CPT Agree']) {
      prefixes.add('agree');
      prefixes.add('disagree');
      prefixes.add('audited_cpt');
      prefixes.add('audited_modifier');
    } else if (cptAuditedMode === 'agreeAll') {
      prefixes.add('agree');
      prefixes.add('disagree');
      prefixes.add('audited_cpt');
      prefixes.add('audited_modifier');
      prefixes.add('audited_rvu');
      prefixes.add('audited_dollar_value');
    }

    return prefixes;
  };

  const handleMappingSubmit = () => {
    setProgressMessage('Processing the mapped data...');
    setProgress(50);

    try {
      const auditedConfig = {
        icdMode: icdAuditedMode,
        cptMode: cptAuditedMode,
        icdCommaSeparated: auditedIcdCommaSeparated,
        cptCommaSeparated: auditedCptCommaSeparated,
        modifierAttached: auditedCptModifierAttached,
        additionalIcdMappings: additionalAuditedIcdMappings,
        additionalCptMappings: additionalAuditedCptMappings,
        additionalModifierMappings: additionalAuditedModifierMappings,
      };
      const processedData = processSheedData(json, headerMapping, checkboxState, providerOptions, currentUpload, industryCodes, iCDCodes, additionalIcdMappings, additionalCptMappings, additionalModifierMappings, auditedConfig, responseAuditIssues);

      if (processedData.warnings?.length) {
        const warningPreview = processedData.warnings
          .map(w => `Row ${w.row}: "${w.field}" > 200 chars (e.g. "${w.preview}")`)
          .join('\n');
        alert("Some fields exceeded character limits and were cleared:\n\n" + warningPreview);
      }

      if (processedData.hasInvalidCodes) {
        alert("Some CPT/ICD codes were not recognized and were left blank.");
      }

      processedData._importMeta = { mappedPrefixes: getMappedPrefixes() };
      updateAuditSheet(processedData);
      setProgress(100);
      setProgressMessage('File has been successfully imported!');
      setIsImported(true);
      setShowMappingUI(false);
    } catch (err) {
      setError(err.message || 'Error processing data.');
      setShowMappingUI(false);
      setProgress(0);
    }
  };

  const handleCheckboxChange = (key, value) => {
    setCheckboxState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const renderMappingCard = (header) => (
    <Box key={header} borderWidth={1} borderRadius="md" borderColor="gray.300" p={4} bg="white" boxShadow="sm">
      <HStack spacing={1.5} mb={2} align="center">
        <Text fontSize="sm" fontWeight="medium" color="gray.700">
          {cardTitles[header] || header}
        </Text>
        {headerTooltips[header] && (
          <Tooltip label={headerTooltips[header]} hasArrow placement="top">
            <span style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
              <FiInfo size={13} color="#718096" />
            </span>
          </Tooltip>
        )}
      </HStack>
      <Select
        placeholder="Select Excel Column"
        onChange={(e) => handleHeaderMappingChange(header, e.target.value)}
        value={headerMapping[header] || ''}
        variant="filled"
        color="gray.700"
        bg="gray.100"
        borderColor="gray.300"
        _hover={{ borderColor: 'blue.400' }}
      >
        {jsonHeaders.map((jsonHeader) => (
          <option key={jsonHeader} value={jsonHeader}>
            {jsonHeader}
          </option>
        ))}
      </Select>

      {header === 'Billed ICD' && (
        <VStack mt={3} align="start" spacing={2} width="100%">
          <Checkbox
            isChecked={checkboxState.ICDCommaSeparated}
            onChange={(e) => handleCheckboxChange('ICDCommaSeparated', e.target.checked)}
            colorScheme="blue"
            size="sm"
          >
            Comma Separated
          </Checkbox>
          {!checkboxState.ICDCommaSeparated && (
            <VStack align="start" spacing={1} width="100%">
              {additionalIcdMappings.map((col, idx) => (
                <HStack key={idx} width="100%">
                  <Select
                    size="sm"
                    placeholder="Select ICD Column"
                    value={col}
                    onChange={(e) => handleAdditionalIcdChange(idx, e.target.value)}
                    flex={1}
                    variant="filled"
                    color="gray.700"
                    bg="gray.100"
                  >
                    {jsonHeaders.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </Select>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => removeAdditionalIcdColumn(idx)}
                    px={2}
                  >
                    ×
                  </Button>
                </HStack>
              ))}
              <Button
                size="xs"
                variant="link"
                colorScheme="blue"
                onClick={addAdditionalIcdColumn}
              >
                + Add ICD Column
              </Button>
            </VStack>
          )}
        </VStack>
      )}

      {header === 'Billed CPT' && (
        <VStack mt={3} align="start" spacing={2} width="100%">
          <Checkbox
            isChecked={checkboxState.cptModifierAttached}
            onChange={(e) => handleCheckboxChange('cptModifierAttached', e.target.checked)}
            colorScheme="blue"
            size="sm"
          >
            Modifier attached to CPT
          </Checkbox>
          <Checkbox
            isChecked={checkboxState.CPTCommaSeparated}
            onChange={(e) => handleCheckboxChange('CPTCommaSeparated', e.target.checked)}
            colorScheme="blue"
            size="sm"
          >
            Comma Separated
          </Checkbox>

          {!checkboxState.cptModifierAttached && (
            <Box width="100%">
              <Text fontSize="xs" color="gray.500" mb={1}>
                {checkboxState.CPTCommaSeparated
                  ? 'Modifier column (comma-separated, matched by position to each CPT)'
                  : 'Primary Modifier column (paired with the primary CPT column)'}
              </Text>
              <Select
                size="sm"
                placeholder="Select Modifier Column"
                onChange={(e) => handleHeaderMappingChange('Modifier', e.target.value)}
                value={headerMapping['Modifier'] || ''}
                variant="filled"
                color="gray.700"
                bg="gray.100"
              >
                {jsonHeaders.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </Select>
            </Box>
          )}

          {!checkboxState.CPTCommaSeparated && (
            <VStack align="start" spacing={1} width="100%">
              {additionalCptMappings.map((col, idx) => (
                <HStack key={idx} width="100%">
                  <Select
                    size="sm"
                    placeholder="Select CPT Column"
                    value={col}
                    onChange={(e) => handleAdditionalCptChange(idx, e.target.value)}
                    flex={1}
                    variant="filled"
                    color="gray.700"
                    bg="gray.100"
                  >
                    {jsonHeaders.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </Select>
                  {!checkboxState.cptModifierAttached && (
                    <Select
                      size="sm"
                      placeholder="Select Modifier Column"
                      value={additionalModifierMappings[idx] || ''}
                      onChange={(e) => handleAdditionalModifierChange(idx, e.target.value)}
                      flex={1}
                      variant="filled"
                      color="gray.700"
                      bg="gray.100"
                    >
                      {jsonHeaders.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </Select>
                  )}
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => removeAdditionalCptModifierColumn(idx)}
                    px={2}
                  >
                    ×
                  </Button>
                </HStack>
              ))}
              <Button
                size="xs"
                variant="link"
                colorScheme="blue"
                onClick={addAdditionalCptModifierColumn}
              >
                + Add CPT{!checkboxState.cptModifierAttached ? ' & Modifier' : ''} Column
              </Button>
            </VStack>
          )}
        </VStack>
      )}
    </Box>
  );

  return (
    <>
      <Button
        colorScheme="teal"
        leftIcon={<FaFileUpload />}
        onClick={openModal}
        isDisabled={isLoading}
      >
        {isLoading ? 'Uploading...' : 'Import Sheet'}
      </Button>

      <Modal isOpen={isOpen} onClose={closeModal} size="5xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>Import Excel File</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            {showMappingUI ? (
              <VStack spacing={6} align="stretch">
                <Text fontSize="xl" fontWeight="bold" color="gray.800" textAlign="center">
                  Map Excel to Rev. Sheet Columns
                </Text>

                <Grid templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }} gap={4}>
                  {encounterHeaders.map(renderMappingCard)}
                </Grid>

                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
                  {billedCodeHeaders.map(renderMappingCard)}
                </Grid>

                {/* ── Audited Fields Section ──────────────────────────── */}
                <Box>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={showAuditedSection ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                    onClick={() => setShowAuditedSection(v => !v)}
                    px={2}
                  >
                    Configure Audited Fields
                  </Button>

                  {showAuditedSection && (
                    <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4} mt={3}>

                      {/* Audited ICD card */}
                      <Box borderWidth={1} borderRadius="md" borderColor="gray.300" p={4} bg="white" boxShadow="sm">
                        <HStack spacing={1.5} mb={3} align="center">
                          <Text fontSize="sm" fontWeight="medium" color="gray.700">Audited ICD</Text>
                          <Tooltip label={headerTooltips['Audited ICD']} hasArrow placement="top">
                            <span style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                              <FiInfo size={13} color="#718096" />
                            </span>
                          </Tooltip>
                        </HStack>

                        <VStack align="start" spacing={3} width="100%">
                            <Select
                              size="sm"
                              placeholder="Select mode..."
                              value={icdAuditedMode}
                              onChange={(e) => handleIcdAuditedModeChange(e.target.value)}
                              variant="filled"
                              color="gray.700"
                              bg="gray.100"
                            >
                              <option value="importColumns">Import Audited ICD Columns</option>
                              <option value="agreeColumn">Use ICD Agree Column</option>
                              <option value="agreeAll">Agree All (copy every billed ICD)</option>
                            </Select>

                            {icdAuditedMode === 'agreeAll' && (
                              <Text fontSize="xs" color="gray.500">
                                Every billed ICD is copied to audited ICD and marked agreed — no source column needed.
                              </Text>
                            )}

                            {icdAuditedMode === 'importColumns' && (
                              <VStack align="start" spacing={2} width="100%">
                                <Select
                                  size="sm"
                                  placeholder="Select Primary Audited ICD Column"
                                  onChange={(e) => handleHeaderMappingChange('Audited ICD', e.target.value)}
                                  value={headerMapping['Audited ICD'] || ''}
                                  variant="filled"
                                  color="gray.700"
                                  bg="gray.100"
                                >
                                  {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                </Select>
                                <Checkbox
                                  size="sm"
                                  colorScheme="blue"
                                  isChecked={auditedIcdCommaSeparated}
                                  onChange={(e) => setAuditedIcdCommaSeparated(e.target.checked)}
                                >
                                  Comma Separated
                                </Checkbox>
                                {!auditedIcdCommaSeparated && (
                                  <VStack align="start" spacing={1} width="100%">
                                    {additionalAuditedIcdMappings.map((col, idx) => (
                                      <HStack key={idx} width="100%">
                                        <Select
                                          size="sm"
                                          placeholder="Select Audited ICD Column"
                                          value={col}
                                          onChange={(e) => handleAdditionalAuditedIcdChange(idx, e.target.value)}
                                          flex={1}
                                          variant="filled"
                                          color="gray.700"
                                          bg="gray.100"
                                        >
                                          {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                        </Select>
                                        <Button size="xs" variant="ghost" colorScheme="red" onClick={() => removeAdditionalAuditedIcdColumn(idx)} px={2}>×</Button>
                                      </HStack>
                                    ))}
                                    <Button size="xs" variant="link" colorScheme="blue" onClick={addAdditionalAuditedIcdColumn}>
                                      + Add Audited ICD Column
                                    </Button>
                                  </VStack>
                                )}
                              </VStack>
                            )}

                            {icdAuditedMode === 'agreeColumn' && (
                              <VStack align="start" spacing={1} width="100%">
                                <Select
                                  size="sm"
                                  placeholder="Select ICD Agree Column"
                                  onChange={(e) => handleHeaderMappingChange('ICD Agree', e.target.value)}
                                  value={headerMapping['ICD Agree'] || ''}
                                  variant="filled"
                                  color="gray.700"
                                  bg="gray.100"
                                >
                                  {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                </Select>
                                <Text fontSize="xs" color="gray.500">
                                  Yes → copies billed ICD to audited ICD and sets ICD Agree. No → leaves audited blank.
                                </Text>
                              </VStack>
                            )}
                          </VStack>
                      </Box>

                      {/* Audited CPT & Modifier card */}
                      <Box borderWidth={1} borderRadius="md" borderColor="gray.300" p={4} bg="white" boxShadow="sm">
                        <HStack spacing={1.5} mb={3} align="center">
                          <Text fontSize="sm" fontWeight="medium" color="gray.700">Audited CPT & Modifier</Text>
                          <Tooltip label={headerTooltips['Audited CPT & Modifier']} hasArrow placement="top">
                            <span style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                              <FiInfo size={13} color="#718096" />
                            </span>
                          </Tooltip>
                        </HStack>

                        <VStack align="start" spacing={3} width="100%">
                            <Select
                              size="sm"
                              placeholder="Select mode..."
                              value={cptAuditedMode}
                              onChange={(e) => handleCptAuditedModeChange(e.target.value)}
                              variant="filled"
                              color="gray.700"
                              bg="gray.100"
                            >
                              <option value="importColumns">Import Audited CPT & Modifier Columns</option>
                              <option value="agreeColumn">Use CPT Agree Column</option>
                              <option value="agreeAll">Agree All (copy every billed CPT & modifier)</option>
                            </Select>

                            {cptAuditedMode === 'agreeAll' && (
                              <Text fontSize="xs" color="gray.500">
                                Every billed CPT & modifier is copied to audited and marked agreed — no source column needed.
                              </Text>
                            )}

                            {cptAuditedMode === 'importColumns' && (
                              <VStack align="start" spacing={2} width="100%">
                                <Select
                                  size="sm"
                                  placeholder="Select Primary Audited CPT Column"
                                  onChange={(e) => handleHeaderMappingChange('Audited CPT', e.target.value)}
                                  value={headerMapping['Audited CPT'] || ''}
                                  variant="filled"
                                  color="gray.700"
                                  bg="gray.100"
                                >
                                  {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                </Select>
                                <Checkbox
                                  size="sm"
                                  colorScheme="blue"
                                  isChecked={auditedCptModifierAttached}
                                  onChange={(e) => setAuditedCptModifierAttached(e.target.checked)}
                                >
                                  Modifier attached to CPT
                                </Checkbox>
                                <Checkbox
                                  size="sm"
                                  colorScheme="blue"
                                  isChecked={auditedCptCommaSeparated}
                                  onChange={(e) => setAuditedCptCommaSeparated(e.target.checked)}
                                >
                                  Comma Separated
                                </Checkbox>

                                {!auditedCptModifierAttached && (
                                  <Box width="100%">
                                    <Text fontSize="xs" color="gray.500" mb={1}>
                                      {auditedCptCommaSeparated
                                        ? 'Modifier column (comma-separated, matched by position to each CPT)'
                                        : 'Primary Modifier column (paired with the primary CPT column)'}
                                    </Text>
                                    <Select
                                      size="sm"
                                      placeholder="Select Audited Modifier Column"
                                      onChange={(e) => handleHeaderMappingChange('Audited Modifier', e.target.value)}
                                      value={headerMapping['Audited Modifier'] || ''}
                                      variant="filled"
                                      color="gray.700"
                                      bg="gray.100"
                                    >
                                      {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                    </Select>
                                  </Box>
                                )}

                                {!auditedCptCommaSeparated && (
                                  <VStack align="start" spacing={1} width="100%">
                                    {additionalAuditedCptMappings.map((col, idx) => (
                                      <HStack key={idx} width="100%">
                                        <Select
                                          size="sm"
                                          placeholder="Select Audited CPT Column"
                                          value={col}
                                          onChange={(e) => handleAdditionalAuditedCptChange(idx, e.target.value)}
                                          flex={1}
                                          variant="filled"
                                          color="gray.700"
                                          bg="gray.100"
                                        >
                                          {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                        </Select>
                                        {!auditedCptModifierAttached && (
                                          <Select
                                            size="sm"
                                            placeholder="Select Audited Modifier Column"
                                            value={additionalAuditedModifierMappings[idx] || ''}
                                            onChange={(e) => handleAdditionalAuditedModifierChange(idx, e.target.value)}
                                            flex={1}
                                            variant="filled"
                                            color="gray.700"
                                            bg="gray.100"
                                          >
                                            {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                          </Select>
                                        )}
                                        <Button size="xs" variant="ghost" colorScheme="red" onClick={() => removeAdditionalAuditedCptModifierColumn(idx)} px={2}>×</Button>
                                      </HStack>
                                    ))}
                                    <Button size="xs" variant="link" colorScheme="blue" onClick={addAdditionalAuditedCptModifierColumn}>
                                      + Add Audited CPT{!auditedCptModifierAttached ? ' & Modifier' : ''} Column
                                    </Button>
                                  </VStack>
                                )}
                              </VStack>
                            )}

                            {cptAuditedMode === 'agreeColumn' && (
                              <VStack align="start" spacing={1} width="100%">
                                <Select
                                  size="sm"
                                  placeholder="Select CPT Agree Column"
                                  onChange={(e) => handleHeaderMappingChange('CPT Agree', e.target.value)}
                                  value={headerMapping['CPT Agree'] || ''}
                                  variant="filled"
                                  color="gray.700"
                                  bg="gray.100"
                                >
                                  {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                </Select>
                                <Text fontSize="xs" color="gray.500">
                                  Yes → copies billed CPT & modifier to audited and sets CPT Agree. No → leaves audited blank.
                                </Text>
                              </VStack>
                            )}
                          </VStack>
                      </Box>

                    </Grid>
                  )}
                </Box>

                {/* ── Override RVU & Dollar Value (Optional) ───────────── */}
                <Box>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={showRvuSection ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                    onClick={() => setShowRvuSection(v => !v)}
                    px={2}
                  >
                    RVU & Dollar Value
                    <Text as="span" fontSize="xs" color="gray.400" fontWeight="normal" ml={2}>(Optional — auto-calculated from CPT)</Text>
                  </Button>

                  {showRvuSection && (
                    <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4} mt={3}>

                      {[
                        { key: 'Billed RVU',    placeholder: 'Select Billed RVU Column' },
                        { key: 'Billed $ Value', placeholder: 'Select Billed $ Value Column' },
                        { key: 'Audited RVU',   placeholder: 'Select Audited RVU Column' },
                        { key: 'Audited $ Value', placeholder: 'Select Audited $ Value Column' },
                      ].map(({ key, placeholder }) => (
                        <Box key={key} borderWidth={1} borderRadius="md" borderColor="gray.300" p={4} bg="white" boxShadow="sm">
                          <HStack spacing={1.5} mb={3} align="center">
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">{key}</Text>
                            {headerTooltips[key] && (
                              <Tooltip label={headerTooltips[key]} hasArrow placement="top">
                                <span style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                                  <FiInfo size={13} color="#718096" />
                                </span>
                              </Tooltip>
                            )}
                          </HStack>
                          <Select
                            size="sm"
                            placeholder={placeholder}
                            onChange={(e) => handleHeaderMappingChange(key, e.target.value)}
                            value={headerMapping[key] || ''}
                            variant="filled"
                            color="gray.700"
                            bg="gray.100"
                          >
                            {jsonHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                          </Select>
                        </Box>
                      ))}

                    </Grid>
                  )}
                </Box>

                {/* ── Additional fields: Response, Notes, Provider Education, MDM ── */}
                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
                  {additionalHeaders.map((header) => (
                    <Box key={header} borderWidth={1} borderRadius="md" borderColor="gray.300" p={4} bg="white" boxShadow="sm">
                      <HStack spacing={1.5} mb={2} align="center">
                        <Text fontSize="sm" fontWeight="medium" color="gray.700">{header}</Text>
                        {headerTooltips[header] && (
                          <Tooltip label={headerTooltips[header]} hasArrow placement="top">
                            <span style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                              <FiInfo size={13} color="#718096" />
                            </span>
                          </Tooltip>
                        )}
                      </HStack>
                      <Select
                        placeholder="Select Excel Column"
                        onChange={(e) => handleHeaderMappingChange(header, e.target.value)}
                        value={headerMapping[header] || ''}
                        variant="filled"
                        color="gray.700"
                        bg="gray.100"
                        borderColor="gray.300"
                        _hover={{ borderColor: 'blue.400' }}
                      >
                        {jsonHeaders.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </Select>
                    </Box>
                  ))}
                </Grid>

              </VStack>
            ) : (isAtaching || isLoading) ? (
              <VStack spacing={6} align="center">
                {isAtaching && (
                  <VStack spacing={4} align="center" width="100%">
                    <Text fontSize="lg" fontWeight="medium">{atachingMsg}</Text>
                    <Progress value={attachingProgress} size="xs" colorScheme="teal" width="100%" />
                    <Text fontSize="sm" color="gray.500">{fileName || 'No file selected'}</Text>
                  </VStack>
                )}
                {isLoading && (
                  <VStack spacing={4} align="center" width="100%">
                    <Text fontSize="lg" fontWeight="medium">{progressMessage}</Text>
                    <Progress value={progress} size="xs" colorScheme="blue" width="100%" />
                    <Text fontSize="sm" color="gray.500">{fileName || 'No file selected'}</Text>
                  </VStack>
                )}
              </VStack>
            ) : isImported ? (
              <Box textAlign="center">
                <Text fontSize="lg" color="green.500" fontWeight="bold">File successfully imported!</Text>
              </Box>
            ) : isAttached ? (
              <Box textAlign="center">
                <Text fontSize="lg" color="green.500" fontWeight="bold">File successfully attached!</Text>
              </Box>
            ) : error ? (
              <Box textAlign="center">
                <Text color="red.500" fontSize="lg" fontWeight="medium">{error}</Text>
              </Box>
            ) : (
              <VStack spacing={5} align="stretch" width="100%" py={2}>
                <VStack spacing={1} align="center">
                  <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                    Import or Attach a Chart
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Choose an action below, then select your Excel file.
                  </Text>
                </VStack>

                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
                  <Box
                    as="button"
                    type="button"
                    onClick={() => setIsImportFile(!isImportFile)}
                    textAlign="left"
                    borderWidth={1}
                    borderRadius="md"
                    borderColor={isImportFile ? 'blue.400' : 'gray.200'}
                    bg="white"
                    boxShadow="sm"
                    p={4}
                    _hover={{ borderColor: 'blue.300' }}
                  >
                    <HStack spacing={3} align="start">
                      <Box color="gray.500" fontSize="18px" mt={0.5}>
                        <FaFileUpload />
                      </Box>
                      <Box flex={1}>
                        <Text fontWeight="semibold" color="gray.800">Import File</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                          Map columns and load rows into the audit sheet.
                        </Text>
                      </Box>
                      <Checkbox isChecked={isImportFile} colorScheme="blue" pointerEvents="none" mt={0.5} />
                    </HStack>
                  </Box>

                  <Box
                    as="button"
                    type="button"
                    onClick={() => setIsAttachFile(!isAttachFile)}
                    textAlign="left"
                    borderWidth={1}
                    borderRadius="md"
                    borderColor={isAttachFile ? 'blue.400' : 'gray.200'}
                    bg="white"
                    boxShadow="sm"
                    p={4}
                    _hover={{ borderColor: 'blue.300' }}
                  >
                    <HStack spacing={3} align="start">
                      <Box color="gray.500" fontSize="18px" mt={0.5}>
                        <FiPaperclip />
                      </Box>
                      <Box flex={1}>
                        <Text fontWeight="semibold" color="gray.800">Attach File</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                          Attach the original file to this chart for reference.
                        </Text>
                      </Box>
                      <Checkbox isChecked={isAttachFile} colorScheme="blue" pointerEvents="none" mt={0.5} />
                    </HStack>
                  </Box>
                </Grid>

                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleFileDrop}
                  borderWidth={2}
                  borderStyle="dashed"
                  borderRadius="md"
                  borderColor={isDragActive ? 'blue.400' : 'gray.300'}
                  bg={isDragActive ? 'blue.50' : 'gray.50'}
                  p={8}
                  textAlign="center"
                  opacity={!isImportFile && !isAttachFile ? 0.5 : 1}
                  transition="border-color 0.15s, background-color 0.15s"
                >
                  <VStack spacing={2}>
                    <Box fontSize="28px" color="gray.400">
                      <FiUploadCloud />
                    </Box>
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      id="file-upload"
                      disabled={!isImportFile && !isAttachFile}
                    />
                    <label htmlFor="file-upload">
                      <Button as="span" colorScheme="blue" leftIcon={<FaFileUpload />} isDisabled={!isImportFile && !isAttachFile}>
                        Browse Files
                      </Button>
                    </label>
                    <Text fontSize="sm" color="gray.500">or drag and drop your Excel file here</Text>
                    <Text fontSize="xs" color="gray.400">Supports .xlsx and .xls</Text>
                    <Text fontSize="sm" color="gray.700" fontWeight="medium" mt={2}>
                      {fileName || 'No file selected'}
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {showMappingUI && (
              <Button
                colorScheme="teal"
                onClick={handleMappingSubmit}
                isLoading={!codeListsLoaded}
                loadingText="Loading code lists..."
                isDisabled={!codeListsLoaded}
              >
                Submit Mapping
              </Button>
            )}
            <Button onClick={closeModal} ml={3}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default ExcelReader;